import { inject } from '@angular/core';
import { CanMatchFn, Router, Routes } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { loggedInGuard } from './logged-in-guard';
import { demoGateGuard } from './demo-gate/demo-gate.guard';
import { featuredListingResolver, browseResolver, listingResolver } from './resolver/listing-resolver';
import { categoryResolver } from './resolver/category-resolver';
import { dealerResolver } from './resolver/dealer-resolver';

const SUPPORTED_LANGUAGES = ['de', 'fr', 'it', 'en'];

const languageMatch: CanMatchFn = (_route, segments) =>
    segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0]!.path);

const langRedirect: CanMatchFn = (_route, segments) => {
    const router = inject(Router);
    const lang = inject(TranslateService).getCurrentLang() || 'de';
    const path = segments.map(s => s.path).join('/');

    // Already has a lang prefix → don't match (avoids infinite loop).
    // Angular will show a 404 / empty page for truly unknown /:lang/xyz paths
    // because publicRoutes simply won't have a child match.
    if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0]!.path)) {
        return router.parseUrl(`/${lang}`);
    }

    // No lang prefix → prepend current language
    return router.parseUrl(`/${lang}/${path}`);
};

const publicRoutes: Routes = [
    // Selling
    { path: 'sell', loadComponent: () => import('./sell/sell').then((m) => m.Sell) },

    // Search
    { path: 'search/dealer', loadComponent: () => import('./search/dealers/dealers').then((m) => m.Dealers) },
    { path: 'search/advanced', loadComponent: () => import('./search/advanced/advanced').then((m) => m.Advanced), resolve: { result: categoryResolver } },
    { path: 'search/advanced/category/:categorySlug', loadComponent: () => import('./search/advanced/advanced').then((m) => m.Advanced), resolve: { result: categoryResolver } },
    { path: 'search/advanced/make/:make', loadComponent: () => import('./search/advanced/advanced').then((m) => m.Advanced), resolve: { result: categoryResolver } },
    { path: 'search/advanced/model/:model', loadComponent: () => import('./search/advanced/advanced').then((m) => m.Advanced), resolve: { result: categoryResolver } },
    { path: 'search/advanced/:firstFilter/:secondFilter', loadComponent: () => import('./search/advanced/advanced').then((m) => m.Advanced), resolve: { result: categoryResolver } },
    { path: 'search/advanced/:firstFilter/:secondFilter/:thirdFilter', loadComponent: () => import('./search/advanced/advanced').then((m) => m.Advanced), resolve: { result: categoryResolver } },

    // Browse
    { path: 'listings/category/:categorySlug', loadComponent: () => import('./listings/listings').then((m) => m.Listings), resolve: { result: browseResolver }, runGuardsAndResolvers: 'paramsOrQueryParamsChange' },
    { path: 'listings/make/:make', loadComponent: () => import('./listings/listings').then((m) => m.Listings), resolve: { result: browseResolver }, runGuardsAndResolvers: 'paramsOrQueryParamsChange' },
    { path: 'listings/model/:model', loadComponent: () => import('./listings/listings').then((m) => m.Listings), resolve: { result: browseResolver }, runGuardsAndResolvers: 'paramsOrQueryParamsChange' },
    { path: 'listings/:firstFilter/:secondFilter', loadComponent: () => import('./listings/listings').then((m) => m.Listings), resolve: { result: browseResolver }, runGuardsAndResolvers: 'paramsOrQueryParamsChange' },
    { path: 'listings/:firstFilter/:secondFilter/:thirdFilter', loadComponent: () => import('./listings/listings').then((m) => m.Listings), resolve: { result: browseResolver }, runGuardsAndResolvers: 'paramsOrQueryParamsChange' },
    { path: 'listings', loadComponent: () => import('./listings/listings').then((m) => m.Listings), resolve: { result: browseResolver }, runGuardsAndResolvers: 'paramsOrQueryParamsChange' },

    // Single listing
    {
        path: 'listings/:slug',
        loadComponent: () => import('./listing/listing').then((m) => m.Listing),
        resolve: { listing: listingResolver }
    },

    // Dealers
    {
        path: 'dealers/:slug',
        loadComponent: () => import('./dealer/dealer-detail/dealer-detail').then((m) => m.DealerDetail),
        resolve: { dealerWithListings: dealerResolver }
    },

    // Prerendered static pages
    { path: 'legal', loadChildren: () => import('./legal/legal.routes') },
    { path: 'help', loadChildren: () => import('./help/help.routes') },

    // Home

    {
        path: 'make/:make',
        loadComponent: () => import('./home/home').then((m) => m.Home),
        resolve: {
            categories: categoryResolver,
            featuredListings: featuredListingResolver
        }
    },
    {
        path: 'model/:model',
        loadComponent: () => import('./home/home').then((m) => m.Home),
        resolve: {
            categories: categoryResolver,
            featuredListings: featuredListingResolver
        }
    },
    {
        path: ':firstFilter/:secondFilter',
        loadComponent: () => import('./home/home').then((m) => m.Home),
        resolve: {
            categories: categoryResolver,
            featuredListings: featuredListingResolver
        }
    },
    {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./home/home').then((m) => m.Home),
        resolve: {
            categories: categoryResolver,
            featuredListings: featuredListingResolver
        }
    },
];

export const routes: Routes = [
    {
        path: 'embed',
        children: [
            {
                path: 'listings',
                loadComponent: () => import('./embed/listings').then((m) => m.EmbedListings),
            },
        ],
    },
    { path: 'auth', loadChildren: () => import('./auth/auth.routes') },
    { path: 'demo-access', loadComponent: () => import('./demo-gate/demo-gate').then(m => m.DemoGate) },
    { path: 'profile', loadChildren: () => import('./profile/profile.routes'), canActivateChild: [loggedInGuard] },
    { path: 'dealer', loadChildren: () => import('./dealer/dealer.routes'), canActivateChild: [loggedInGuard] },

    // Language-prefixed public routes
    { path: ':lang', canMatch: [languageMatch], canActivate: [demoGateGuard], children: publicRoutes },

    // Catch-all: redirect /{path} to /{currentLang}/{path}
    { path: '**', canMatch: [langRedirect], children: [] },
];